import React, { useEffect, useMemo, useRef, useState } from "react";
import Header from "../../components/Header";
import { FileTransfer } from "@capacitor/file-transfer";
import { Filesystem, Directory, FileInfo } from "@capacitor/filesystem";
import { FileOpener } from "@capacitor-community/file-opener";
import { Capacitor } from "@capacitor/core";

import TabNavigation from "../../components/ui/tabs/TabSelector";
import loadingAnimation from "../../assets/icons/loading-animation.svg";
import {
  IonPage,
  IonText,
  IonContent,
  IonIcon,
  IonButton,
  IonModal,
} from "@ionic/react";
import DatePickerWheel from "../../components/ui/Sliders/DatePickerWheel";
import {
  checkmark,
  chevronForward,
  closeOutline,
  eyeOutline,
  shareOutline,
  trashOutline,
} from "ionicons/icons";
import {
  getAuthToken,
  downloadPdfReportNative,
  downloadPdfReportWeb,
  PdfReportParams,
} from "../../services/fileService";
import useAuth from "../../hooks/useAuth";
import { Share } from "@capacitor/share";
import { getWeeklyStatus } from "../../services/dashboard";
import {
  getAllCoughRefids,
  getAllClearRefids,
} from "../../services/userChecklist";
import {
  convertShortDateFormat,
  DateFormat,
  formatDateByConfig,
  formatDateForFilename,
  formatISOByConfig,
  getWheelOrder,
  MONTHS_ABBR,
  parseTabToDateRange,
} from "../../utils/helpers";
import CustomDropdown, {
  DropdownOption,
} from "../../components/dropdown/CustomDropDown";
import ActionButtons from "../../components/ui/popups/ActionButtons";

// ─── Date Format Config ───────────────────────────────────────────────────────
// Change DATE_FORMAT to switch the display format everywhere in the UI.
// Supported values: "MMM DD YYYY" | "DD MMM YYYY" | "YYYY MMM DD"

const DATE_FORMAT: DateFormat = "MMM DD YYYY";

// ─────────────────────────────────────────────────────────────────────────────

type ids = {
  refId: string;
  deviceId: string;
  startTime?: string;
  mode?: string;
};

const SendReport: React.FC = () => {
  const { user } = useAuth();
  const [showDateModal, setShowDateModal] = useState(false);
  const [selectedDateType, setSelectedDateType] = useState<string | undefined>(
    undefined,
  );
  const [fromDay, setFromDay] = useState(0); // Index for day array
  const [fromMonth, setFromMonth] = useState(0); // Index for month array
  const [fromYear, setFromYear] = useState(0); // Index for year array
  const [toDay, setToDay] = useState(0); // Index for day array
  const [toMonth, setToMonth] = useState(0); // Index for month array
  const [toYear, setToYear] = useState(0); // Index for year array
  const [activeDateType, setActiveDateType] = useState<"from" | "to">("from");
  const [showFromDate, setShowFromDate] = useState(false);
  const [showToDate, setShowToDate] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [allRefIds, setAllRefIds] = useState<ids[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState("");
  const [loading, setLoading] = useState(false);
  const [allPDFs, setAllPDFs] = useState<FileInfo[]>([]);
  const [showAllRefIds, setShowAllRefIds] = useState(false);
  const [open, setOpen] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [userName, setUserName] = useState<string>("");
  const [currentTab, setCurrentTab] = useState<string>("cough");
  const [refIDLoading, setRefIDLoading] = useState(false);
  const [oldestCoughDate, setOldestCoughDate] = useState<string>("");
  const [oldestClearDate, setOldestClearDate] = useState<string>("");
  const [initialDateRange, setInitialDateRange] = useState<{ startdate: string; enddate: string; tabValue: string } | null>(null);

  // Fetch oldest therapy dates from API once profileId is known
  useEffect(() => {
    if (!profileId && !user) return;
    let cancelled = false;
    const resolvedProfileId = profileId ? Number(profileId) : user!.profile_id;
    const currentYear = new Date().getFullYear();
    getWeeklyStatus(resolvedProfileId, {
      startDate: `${currentYear}-01-01`,
      endDate: `${currentYear}-12-31`,
      showPercentage: false,
    })
      .then((response: any) => {
        if (cancelled) return;
        const oldest = response?.oldestKnownTherapyDate;
        if (oldest?.cough) setOldestCoughDate(oldest.cough);
        if (oldest?.clear) setOldestClearDate(oldest.clear);
      })
      .catch(() => {
        // silently fall back to URL param values
      });
    return () => { cancelled = true; };
  }, [profileId, user]);

  const graphSpecific = useMemo<DropdownOption[]>(
    () =>
      currentTab === "cough"
        ? [
          { label: "Exp.Flow", value: "PeakExpFlow" },
          { label: "Exp.Volume", value: "ExpVolume" },
          { label: "SpO2", value: "OxygenSaturation" },
          { label: "Heart Rate", value: "HeartRate" },
          { label: "AQI", value: "AQI" },
        ]
        : [
          { label: "Pressure", value: "Pressure" },
          { label: "SpO2", value: "oxygen_saturation" },
          { label: "Heart Rate", value: "heart_rate" },
          { label: "AQI", value: "aqi" },
        ],
    [currentTab],
  );
  const [selectedGraph, setSelectedGraph] = useState<string>(
    graphSpecific[0]?.value
      ? graphSpecific[0]?.value
      : currentTab === "cough"
        ? "PeakExpFlow"
        : "Pressure",
  );

  useEffect(() => {
    if (!graphSpecific.some((option) => option.value === selectedGraph)) {
      setSelectedGraph(graphSpecific[0]?.value || "");
    }
  }, [graphSpecific]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const name = searchParams.get("name");
    if (name) {
      setUserName(name);
    }
    const profile_id = searchParams.get("id");
    if (profile_id) {
      setProfileId(profile_id);
    }
    const tabValue = searchParams.get("tab");
    if (tabValue) {
      setCurrentTab(tabValue);
    }

    const oldestCough = searchParams.get("oldestCough") || "";
    const oldestClear = searchParams.get("oldestClear") || "";
    setOldestCoughDate(oldestCough);
    setOldestClearDate(oldestClear);

    const periodValue = searchParams.get("period");
    const years = Array.from({ length: 30 }, (_, i) =>
      (currentYear - 29 + i).toString(),
    );
    setSelectedDateType("Custom Range");

    let calculatedDateRange = "";
    const yearIndexFor = (yearNum: number) => {
      const idx = years.indexOf(String(yearNum));
      if (idx !== -1) return idx;
      return Math.max(0, Math.min(years.length - 1, yearNum - Number(years[0])));
    };

    if (periodValue && ["Year", "Month", "Week", "Day"].includes(periodValue)) {
      if (periodValue === "Year") {
        const currentYear = new Date().getFullYear();
        const startOfYear = `${currentYear}-01-01`;
        const endOfYear = `${currentYear}-12-31`;
        calculatedDateRange = `${startOfYear} to ${endOfYear}`;
        const startDate = new Date(currentYear, 0, 1);
        const endDate = new Date(currentYear, 11, 31);

        setFromDay(startDate.getDate() - 1);
        setFromMonth(startDate.getMonth());
        setFromYear(yearIndexFor(startDate.getFullYear()));
        setToDay(endDate.getDate() - 1);
        setToMonth(endDate.getMonth());
        setToYear(yearIndexFor(endDate.getFullYear()));
      }
      if (periodValue === "Month") {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = (currentDate.getMonth() + 1)
          .toString()
          .padStart(2, "0");
        const startOfMonth = `${currentYear}-${currentMonth}-01`;
        const endOfMonth = `${currentYear}-${currentMonth}-${new Date(
          currentYear,
          currentDate.getMonth() + 1,
          0,
        )
          .getDate()
          .toString()
          .padStart(2, "0")}`;
        calculatedDateRange = `${startOfMonth} to ${endOfMonth}`;
        setFromDay(0);
        setFromMonth(currentDate.getMonth());
        setFromYear(yearIndexFor(currentDate.getFullYear()));
        setToDay(
          new Date(currentYear, currentDate.getMonth() + 1, 0).getDate() - 1,
        );
        setToMonth(currentDate.getMonth());
        setToYear(yearIndexFor(currentDate.getFullYear()));
      }
      if (periodValue === "Week") {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();

        const dayOfWeek = currentDate.getDay();
        const diff =
          currentDate.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        const monday = new Date(currentYear, currentDate.getMonth(), diff);

        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);

        const mondayMonth = (monday.getMonth() + 1).toString().padStart(2, "0");
        const mondayDay = monday.getDate().toString().padStart(2, "0");
        const sundayYear = sunday.getFullYear();
        const sundayMonth = (sunday.getMonth() + 1).toString().padStart(2, "0");
        const sundayDay = sunday.getDate().toString().padStart(2, "0");

        const startOfWeek = `${currentYear}-${mondayMonth}-${mondayDay}`;
        const endOfWeek = `${sundayYear}-${sundayMonth}-${sundayDay}`;
        calculatedDateRange = `${startOfWeek} to ${endOfWeek}`;
        setFromDay(monday.getDate() - 1);
        setFromMonth(monday.getMonth());
        setFromYear(yearIndexFor(monday.getFullYear()));

        setToDay(sunday.getDate() - 1);
        setToMonth(sunday.getMonth());
        setToYear(yearIndexFor(sunday.getFullYear()));
      }
      if (periodValue === "Day") {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = (currentDate.getMonth() + 1)
          .toString()
          .padStart(2, "0");
        const currentDay = currentDate.getDate().toString().padStart(2, "0");
        const today = `${currentYear}-${currentMonth}-${currentDay}`;
        calculatedDateRange = `${today} to ${today}`;
        setFromDay(currentDate.getDate() - 1);
        setFromMonth(currentDate.getMonth());
        setFromYear(yearIndexFor(currentDate.getFullYear()));
        setToDay(currentDate.getDate() - 1);
        setToMonth(currentDate.getMonth());
        setToYear(yearIndexFor(currentDate.getFullYear()));
      }
    } else {
      calculatedDateRange = parseTabToDateRange(periodValue);

      const [start, end] = calculatedDateRange.split(" to ");

      const [sy, sm, sd] = start.split("-").map(Number);
      const [ey, em, ed] = end.split("-").map(Number);

      setFromYear(yearIndexFor(sy));
      setFromMonth(Math.max(0, Math.min(11, sm - 1)));
      setFromDay(Math.max(0, sd - 1));

      setToYear(yearIndexFor(ey));
      setToMonth(Math.max(0, Math.min(11, em - 1)));
      setToDay(Math.max(0, ed - 1));
    }
    // Clamp end date to today if it's in the future
    const todayStr = new Date().toISOString().split("T")[0];
    const rangeParts = calculatedDateRange.split(" to ");
    if (rangeParts.length === 2 && rangeParts[1] > todayStr) {
      calculatedDateRange = `${rangeParts[0]} to ${todayStr}`;
      const todayDate = new Date();
      setToDay(todayDate.getDate() - 1);
      setToMonth(todayDate.getMonth());
      setToYear(yearIndexFor(todayDate.getFullYear()));
    }

    setDateRange(calculatedDateRange);

    const startdate = calculatedDateRange.split(" to ")[0];
    const enddate = calculatedDateRange.split(" to ")[1];

    setInitialDateRange({ startdate, enddate, tabValue: tabValue || "cough" });
  }, [user]);

  useEffect(() => {
    if (!initialDateRange || !user) return;
    retriveAllRefids(initialDateRange.startdate, initialDateRange.enddate, initialDateRange.tabValue);
  }, [initialDateRange]);

  const [selectedPdfNames, setSelectedPdfNames] = useState<string[]>([]);
  const dateRangeParts = (dateRange || "").split(" to ");
  const startingDate = formatDateForFilename(dateRangeParts[0] || "");
  const endingDate = formatDateForFilename(dateRangeParts[1] || "");
  const folder = `ABMReports/${currentTab}`;

  const fileNamePrefix = userName;
  const tabLabel = currentTab.charAt(0).toUpperCase() + currentTab.slice(1);
  const baseFileName = `${fileNamePrefix}_BiWaze_${tabLabel}_${startingDate}_${endingDate}`;
  let fileName = `${baseFileName}.pdf`;
  if (allPDFs.some((file) => file.name === fileName)) {
    let counter = 1;
    let newFileName = `${baseFileName}(${counter}).pdf`;
    while (allPDFs.some((file) => file.name === newFileName)) {
      counter++;
      newFileName = `${baseFileName}(${counter}).pdf`;
    }
    fileName = newFileName;
  }
  useEffect(() => {
    let progressListener: any;
    const addProgressListener = async () => {
      progressListener = await FileTransfer.addListener(
        "progress",
        (progress) => {
          // Some plugins may emit progress events with undefined contentLength
          if (
            progress &&
            typeof progress.bytes === "number" &&
            typeof progress.contentLength === "number" &&
            progress.contentLength > 0
          ) {
            setDownloadProgress(
              Math.round((progress.bytes / progress.contentLength) * 100),
            );
          }
        },
      );
    };
    addProgressListener();
    return () => {
      if (progressListener && typeof progressListener.remove === "function") {
        progressListener.remove();
      }
    };
  }, []);

  const availableTabs = useMemo(() => {
    const tabs: { id: "cough" | "clear"; label: string }[] = [];
    tabs.push({ id: "cough", label: "BiWaze® Cough" });
    tabs.push({ id: "clear", label: "BiWaze® Clear" });
    return tabs;
  }, []);
  useEffect(() => {
    const requestPermissions = async () => {
      const permStatus = await Filesystem.checkPermissions();
      if (permStatus.publicStorage !== "granted") {
        await Filesystem.requestPermissions();
      }
    };
    requestPermissions();
  }, []);
  const isFirstRenderRef = useRef(true);
  const refIdFetchIdRef = useRef(0);

  // Effect 1: fetch ref IDs on tab change (skip first render)
  useEffect(() => {
    if (isFirstRenderRef.current) return;
    setSelectedIds([]);
    retriveAllRefids(
      `${years[fromYear]}-${fromMonth + 1}-${fromDays[fromDay]}`,
      `${years[toYear]}-${toMonth + 1}-${toDays[toDay]}`,
      currentTab || "cough",
    );
  }, [currentTab]);

  // Effect 2: fetch local PDFs whenever the folder (tab) changes
  useEffect(() => {
    let cancelled = false;
    const fetchAllPdfs = async () => {
      try {
        const PdfList = await Filesystem.readdir({
          path: folder,
          directory: Directory.Documents,
        });
        if (cancelled) return;
        setAllPDFs([...PdfList.files].sort((a, b) => (b.ctime || 0) - (a.ctime || 0)));
      } catch {
        if (!cancelled) setAllPDFs([]);
      }
    };
    fetchAllPdfs();
    isFirstRenderRef.current = false;
    return () => { cancelled = true; };
  }, [folder]);
  const dateOptions = ["Last Week", "Last 30 days", "Custom Range"];

  // Generate days for "to" date picker
  const getDaysInMonth = (monthIndex: number, yearIndex: number) => {
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 30 }, (_, i) => currentYear - 29 + i);
    const actualYear = years[yearIndex];
    const actualMonth = monthIndex; // 0-based month
    return new Date(actualYear, actualMonth + 1, 0).getDate();
  };
  const toDaysCount = getDaysInMonth(toMonth, toYear);
  const toDays = Array.from({ length: toDaysCount }, (_, i) =>
    (i + 1).toString().padStart(2, "0"),
  );

  // Handle day adjustment when month or year changes
  const adjustDayIfNeeded = (dayIndex: number, maxDays: number) => {
    return Math.min(dayIndex, maxDays - 1);
  };

  // Handle date type change
  const handleDateTypeChange = (newDateType: string) => {
    setSelectedDateType(newDateType);
  };

  // Generate years: from (current year - 29) to current year, as strings
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 30 }, (_, i) =>
    (currentYear - 29 + i).toString(),
  );

  // Format date for display — proxies through DATE_FORMAT config
  const formatDateDisplay = (
    dayIndex: number,
    monthIndex: number,
    yearIndex: number,
    daysArray: string[],
  ) =>
    formatDateByConfig(
      dayIndex,
      monthIndex,
      yearIndex,
      daysArray,
      MONTHS_ABBR,
      years,
      DATE_FORMAT,
    );
  // Generate days for "from" date picker
  const fromDaysCount = getDaysInMonth(fromMonth, fromYear);
  const fromDays = Array.from({ length: fromDaysCount }, (_, i) =>
    (i + 1).toString().padStart(2, "0"),
  );
  const retriveAllRefids = async (
    startdate: string,
    enddate: string,
    tabValue: string,
  ) => {
    if (!user) return;
    const fetchId = ++refIdFetchIdRef.current;
    setRefIDLoading(true);
    setAllRefIds([]);
    try {
      let response;
      if (tabValue === "cough") {
        response = await getAllCoughRefids(
          profileId ? profileId : user.profile_id,
          startdate || new Date().toISOString().split("T")[0],
          enddate || new Date().toISOString().split("T")[0],
          user.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        );
      } else {
        response = await getAllClearRefids(
          profileId ? profileId : user.profile_id,
          startdate || new Date().toISOString().split("T")[0],
          enddate || new Date().toISOString().split("T")[0],
          user.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        );
      }
      if (fetchId !== refIdFetchIdRef.current) return;
      setAllRefIds(
        [...(response.data.refIds || [])].sort((a: ids, b: ids) => {
          const timeA = a.startTime ? new Date(a.startTime).getTime() : -Infinity;
          const timeB = b.startTime ? new Date(b.startTime).getTime() : -Infinity;
          // Treat NaN (invalid dates) as -Infinity to push them to the end
          const validTimeA = isNaN(timeA) ? -Infinity : timeA;
          const validTimeB = isNaN(timeB) ? -Infinity : timeB;
          return validTimeB - validTimeA; // latest first
        }),
      );
      setRefIDLoading(false);
    } catch (error) {
      if (fetchId !== refIdFetchIdRef.current) return;
      console.error("Error fetching RefIds:", error);
      setRefIDLoading(false);
    }
  };

  const onselectRange = async () => {
    setSelectedIds([]);
    const today = new Date();
    try {
      if (selectedDateType === "Today") {
        const todayStr = today.toISOString().split("T")[0] || "";
        setDateRange(`${todayStr} to ${todayStr}`);
        retriveAllRefids(todayStr, todayStr, currentTab ? currentTab : "cough");
      } else if (selectedDateType === "Last Week") {
        const startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 7);
        const endDate = new Date(today);
        const startStr = startDate.toISOString().split("T")[0] || "";
        const endStr = endDate.toISOString().split("T")[0] || "";
        setDateRange(`${startStr} to ${endStr}`);
        retriveAllRefids(startStr, endStr, currentTab ? currentTab : "cough");
      } else if (selectedDateType === "Last 30 days") {
        const startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 30);
        const endDate = new Date(today);
        const startStr = startDate.toISOString().split("T")[0] || "";
        const endStr = endDate.toISOString().split("T")[0] || "";
        setDateRange(`${startStr} to ${endStr}`);
        retriveAllRefids(startStr, endStr, currentTab ? currentTab : "cough");
      } else if (selectedDateType === "Custom Range") {
        const fromDateStr = `${years[fromYear] || ""}-${(fromMonth + 1)
          .toString()
          .padStart(2, "0")}-${fromDays[fromDay] || ""}`;
        const toDateStr = `${years[toYear] || ""}-${(toMonth + 1)
          .toString()
          .padStart(2, "0")}-${toDays[toDay] || ""}`;
        setDateRange(`${fromDateStr} to ${toDateStr}`);
        retriveAllRefids(
          fromDateStr,
          toDateStr,
          currentTab ? currentTab : "cough",
        );
      }
    } catch (err) {
      console.error("Error computing date range:", err);
    }
  };
  const [alertMessage, setAlertMessage] = useState("");

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        setAlertMessage("");
        return prev.filter((v) => v !== id);
      } else if (prev.length >= 15) {
        setAlertMessage("Cannot select more than 15 Ref IDs.");
        return prev;
      } else {
        setAlertMessage("");
        return [...prev, id];
      }
    });
  };

  useEffect(() => {
    if (alertMessage) {
      const timer = setTimeout(() => setAlertMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [alertMessage]);

  const handleOpenPdf = async (e: any, fileName: string) => {
    e.stopPropagation();
    try {
      const fileUriResult = await Filesystem.getUri({
        path: `${folder}/${fileName}`,
        directory: Directory.Documents,
      });
      await FileOpener.open({
        filePath: fileUriResult.uri,
        contentType: "application/pdf",
      });
    } catch (err) {
      console.error("Error opening PDF:", err);
    }
  };
  const handleSharePdf = async (
    e: React.MouseEvent<HTMLIonIconElement>,
    fileName: string[],
  ) => {
    e.stopPropagation();

    try {
      const shareFiles: string[] = [];

      const filesToShare = fileName.length > 0 ? fileName : selectedPdfNames;

      for (const name of filesToShare) {
        const cacheFileUriResult = await Filesystem.getUri({
          path: `${folder}/${name}`,
          directory: Directory.Documents,
        });

        shareFiles.push(cacheFileUriResult.uri);
      }

      await Share.share({
        title: "BiWaze® Therapy Report",
        files: shareFiles,
      });
    } catch (err) {
      console.error("Error sharing PDF:", err);
    }
  };

  const handleDeletePdf = async (pdfNames: string[]) => {
    try {
      for (const name of pdfNames) {
        await Filesystem.deleteFile({
          path: `${folder}/${name}`,
          directory: Directory.Documents,
        });
      }
      setAllPDFs((prev) => prev.filter((f) => !pdfNames.includes(f.name)));
      setSelectedPdfNames([]);
    } catch (err) {
      console.error("Error deleting PDFs:", err);
    }
  };

  const [showDeletePdf, setShowDeletePdf] = useState(false);
  const renderDeletePdf = (pdfName: string[]) => {
    // Show only first 2 names, rest as "+N more"
    const displayNames = pdfName.slice(0, 2).join(", ");
    const moreCount = pdfName.length > 2 ? ` +${pdfName.length - 2} more` : "";
    return (
      <ActionButtons
        isOpen={showDeletePdf}
        onClose={() => setShowDeletePdf(false)}
        title="Delete PDF Report"
        name={`${displayNames}${moreCount}`}
        message={`Do you want to delete this PDF report Permanently?`}
        actionButtons={[
          {
            text: "Delete",
            action: () => {
              handleDeletePdf(pdfName);
              setShowDeletePdf(false);
            },
            isPrimary: true,
          },
          {
            text: "Cancel",
            action: () => {
              setShowDeletePdf(false);
              setSelectedIds([]);
            },

            isPrimary: false,
          },
        ]}
      />
    );
  };
  const handleGenerateReport = async () => {
    if (!user) return;
    setLoading(true);
    setOpen(false);

    const isNative = Capacitor.isNativePlatform();

    try {
      const startDate = dateRange.split(" to ")[0];
      const endDate = dateRange.split(" to ")[1];

      const token = await getAuthToken();

      // Unique file name (used as the userName param and download filename)
      let uniqueFileName = fileName;

      if (isNative) {
        if (!token) throw new Error("No auth token found for native download");
        // ── Native (iOS / Android) ──────────────────────────────────────────
        // Create the folder if needed
        try {
          await Filesystem.mkdir({
            path: folder,
            directory: Directory.Documents,
            recursive: true,
          });
        } catch (mkdirErr) {
          if (
            !mkdirErr ||
            !(mkdirErr as { code?: string }).code ||
            (mkdirErr as { code?: string }).code !== "OS-PLUG-FILE-0010"
          ) {
            throw mkdirErr;
          }
        }

        // Find a unique file name if a duplicate already exists on disk
        let counter = 1;
        let fileExists = true;
        while (fileExists) {
          try {
            await Filesystem.stat({
              path: `${folder}/${uniqueFileName}`,
              directory: Directory.Documents,
            });
            const extIndex = fileName.lastIndexOf(".pdf");
            uniqueFileName =
              extIndex !== -1
                ? fileName.slice(0, extIndex) +
                `(${counter})` +
                fileName.slice(extIndex)
                : fileName + `(${counter})`;
            counter++;
          } catch {
            fileExists = false;
          }
        }

        const fileUriResult = await Filesystem.getUri({
          path: `${folder}/${uniqueFileName}`,
          directory: Directory.Documents,
        });

        const reportParams: PdfReportParams = {
          viewingProfileId: profileId ?? user.profile_id,
          startDate,
          endDate,
          timeZone:
            user.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          refIds: selectedIds.join(","),
          graphType: selectedGraph,
          showMinMax: false,
          userName: uniqueFileName.replace(".pdf", ""),
          deviceType: currentTab as "clear" | "cough",
        };

        setDownloadProgress(0);
        const startTime = Date.now();

        const downloadResponse = await downloadPdfReportNative(
          reportParams,
          token,
          fileUriResult.uri,
        );

        console.log(
          "Download time:",
          (Date.now() - startTime) / 1000,
          "seconds",
        );

        if (downloadResponse?.path) {
          try {
            await FileOpener.open({
              filePath: downloadResponse.path,
              contentType: "application/pdf",
            });
          } catch (openErr) {
            console.error("Error opening PDF file:", openErr);
          }
        }

        const PdfList = await Filesystem.readdir({
          path: folder,
          directory: Directory.Documents,
        });
        setAllPDFs(
          [...PdfList.files].sort((a, b) => (b.ctime || 0) - (a.ctime || 0)),
        );
      } else {
        // ── Web ────────────────────────────────────────────────────────────
        const webReportParams: PdfReportParams = {
          viewingProfileId: profileId ?? user.profile_id,
          startDate,
          endDate,
          timeZone:
            user.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          refIds: selectedIds.join(","),
          graphType: selectedGraph,
          showMinMax: false,
          userName: uniqueFileName.replace(".pdf", ""),
          deviceType: currentTab as "clear" | "cough",
        };

        setDownloadProgress(0);

        const blob = await downloadPdfReportWeb(
          webReportParams,
          token,
          setDownloadProgress,
        );

        // Trigger browser save-file dialog
        const objectUrl = URL.createObjectURL(
          new Blob([blob], { type: "application/pdf" }),
        );
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = uniqueFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
      }

      setOpen(false);
      setSelectedIds([]);
    } catch (error) {
      console.error("Error generating report:", error);
    } finally {
      setLoading(false);
      // Ensure FileTransfer cleanup happens for native platform
      if (isNative) {
        try {
          await FileTransfer.removeAllListeners();
        } catch (cleanupError) {
          console.error(
            "Error cleaning up FileTransfer listeners:",
            cleanupError,
          );
        }
      }
    }
  };

  const todayForSnap = new Date();
  const todayYearNum = todayForSnap.getFullYear();
  const todayMonthNum = todayForSnap.getMonth(); // 0-indexed
  const todayDayNum = todayForSnap.getDate();    // 1-indexed
  const todayYearIndex = years.indexOf(String(todayYearNum));

  const renderDateRangeModal = () => (
    <IonModal
      isOpen={showDateModal}
      onDidDismiss={() => setShowDateModal(false)}
      presentingElement={document.querySelector("ion-app") || undefined}
      canDismiss={true}
      showBackdrop={true}
      backdropDismiss={true}
      style={{
        "--background": "transparent",
        "--box-shadow": "none",
        "--border-radius": "20px",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "6%",
          left: 0,
          right: 0,
          backgroundColor: "white",
          borderRadius: "20px",
          overflowY: "auto",
          overflowX: "hidden",
          margin: "10px",
          boxSizing: "border-box",
          bottom: "0px",
          height: "max-content",
          maxHeight: "83vh",
        }}
      >
        {/* Header with close button */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            padding: "10px",
            alignItems: "center",
            marginLeft: "20px",
          }}
        >
          <h2
            style={{
              fontSize: "20px",
              fontWeight: "bold",
              margin: 0,
              flex: 1,
              textAlign: "center",
            }}
          >
            Date Range
          </h2>
          <button
            onClick={() => setShowDateModal(false)}
            style={{
              background: "none",
              border: "none",
              fontSize: "24px",
              color: "#666",
              cursor: "pointer",
              padding: "5px",
            }}
          >
            <IonIcon icon={closeOutline} />
          </button>
        </div>

        {/* Date Options */}
        <div style={{ marginBottom: "20px", margin: "10px" }}>
          {dateOptions.map((option) => (
            <div
              key={option}
              className="flex items-center justify-between py-4 border-b"
              style={{
                cursor: "pointer",
                background:
                  selectedDateType === option ? "#f5faff" : "transparent",
                borderBottom: "1px solid #f0f0f0",
                padding: "10px 0",
              }}
              onClick={() => {
                handleDateTypeChange(option);
                if (option === "Custom Range") {
                  setShowFromDate(true);
                  setShowToDate(false);
                  setActiveDateType("from");
                }
              }}
            >
              <span
                className="text-lg"
                style={{ fontSize: 16, fontWeight: 500 }}
              >
                {option}
                {option === "Custom Range" &&
                  selectedDateType === "Custom Range" && (
                    <div
                      style={{ fontSize: 12, color: "#666", marginTop: "2px" }}
                    >
                      {formatDateDisplay(fromDay, fromMonth, fromYear, fromDays)}
                      {" - "}
                      {formatDateDisplay(toDay, toMonth, toYear, toDays)}
                    </div>
                  )}
              </span>
              {selectedDateType === option && (
                <div
                  className="bg-gray-400 rounded-full p-1 flex items-center justify-center w-6 h-6"
                  style={{
                    background: "#05ACD9",
                    borderRadius: "9999px",
                    width: 24,
                    height: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginLeft: 8,
                  }}
                >
                  <div className="bg-gray-400 rounded-full p-1 flex items-center justify-center w-6 h-6">
                    <IonIcon icon={checkmark} style={{ color: "white" }} />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Custom Range Section */}
        {selectedDateType === "Custom Range" && (
          <div style={{ marginBottom: "10px", margin: "10px" }}>
            {/* Start Date Button */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                paddingLeft: "10px",
                paddingRight: "10px",
                paddingTop: "12px",
                paddingBottom: "12px",
                borderBottom: "1px solid #f0f0f0",
              }}
              onClick={() => {
                setActiveDateType("from");
                setShowFromDate(true);
                setShowToDate(false);
              }}
            >
              <label
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#000",
                }}
              >
                Start date
              </label>
              <button
                style={{
                  fontSize: "14px",
                  color: showFromDate ? "#0778CF" : "#000000",
                  fontWeight: 500,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}

              >
                {formatDateDisplay(fromDay, fromMonth, fromYear, fromDays)}
              </button>
            </div>

            {/* From Date Picker */}
            {activeDateType === "from" && showFromDate && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "10px",
                  padding: "15px",
                  borderRadius: "16px",
                  marginBottom: "-23px",
                }}
              >
                {getWheelOrder(DATE_FORMAT).map((col) => {
                  if (col === "MMM") return (
                    <DatePickerWheel
                      key="MMM"
                      items={MONTHS_ABBR}
                      selectedIndex={fromMonth}
                      onSelect={(index: number) => {
                        setFromMonth(index);
                        const newMaxDays = getDaysInMonth(index, fromYear);
                        const adjustedDay = adjustDayIfNeeded(fromDay, newMaxDays);
                        if (adjustedDay !== fromDay) setFromDay(adjustedDay);
                      }}
                      label="MONTH"
                      isActive={true}
                    />
                  );
                  if (col === "DD") return (
                    <DatePickerWheel
                      key="DD"
                      items={fromDays}
                      selectedIndex={fromDay}
                      onSelect={setFromDay}
                      label="DAY"
                      isActive={true}
                    />
                  );
                  return (
                    <DatePickerWheel
                      key="YYYY"
                      items={years}
                      selectedIndex={fromYear}
                      onSelect={(index: number) => {
                        setFromYear(index);
                        const newMaxDays = getDaysInMonth(fromMonth, index);
                        const adjustedDay = adjustDayIfNeeded(fromDay, newMaxDays);
                        if (adjustedDay !== fromDay) setFromDay(adjustedDay);
                      }}
                      label="YEAR"
                      isActive={true}
                    />
                  );
                })}
              </div>
            )}

            {/* End Date Button */}
            <div style={{ marginBottom: "-23px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingLeft: "10px",
                  paddingRight: "10px",
                  paddingTop: "12px",
                  paddingBottom: "12px",
                  borderBottom: "1px solid #f0f0f0",
                }}
                onClick={() => {
                  setActiveDateType("to");
                  setShowFromDate(false);
                  setShowToDate(true);
                }}
              >
                <label
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#000",
                  }}
                >
                  End date
                </label>
                <button
                  style={{
                    fontSize: "14px",
                    color: showToDate ? "#0778CF" : "#000000",
                    fontWeight: 500,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                  }}

                >
                  {formatDateDisplay(toDay, toMonth, toYear, toDays)}
                </button>
              </div>

              {/* To Date Picker */}
              {activeDateType === "to" && showToDate && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "10px",
                    padding: "15px",
                    borderRadius: "16px",
                    // marginBottom: "-46px",
                  }}
                >
                  {getWheelOrder(DATE_FORMAT).map((col) => {
                    if (col === "MMM") return (
                      <DatePickerWheel
                        key="MMM"
                        items={MONTHS_ABBR}
                        selectedIndex={toMonth}
                        onSelect={(index: number) => {
                          const selectedYearNum = parseInt(years[toYear]);
                          if (selectedYearNum === todayYearNum && index > todayMonthNum) {
                            // Snap: future month → clamp to today
                            setToMonth(todayMonthNum);
                            if (toDay > todayDayNum - 1) setToDay(todayDayNum - 1);
                          } else {
                            setToMonth(index);
                            const newMaxDays = getDaysInMonth(index, toYear);
                            let adjustedDay = adjustDayIfNeeded(toDay, newMaxDays);
                            if (selectedYearNum === todayYearNum && index === todayMonthNum && adjustedDay > todayDayNum - 1) {
                              adjustedDay = todayDayNum - 1;
                            }
                            if (adjustedDay !== toDay) setToDay(adjustedDay);
                          }
                        }}
                        label="MONTH"
                        isActive={true}
                      />
                    );
                    if (col === "DD") return (
                      <DatePickerWheel
                        key="DD"
                        items={toDays}
                        selectedIndex={toDay}
                        onSelect={(index: number) => {
                          const selectedYearNum = parseInt(years[toYear]);
                          if (selectedYearNum === todayYearNum && toMonth === todayMonthNum && index > todayDayNum - 1) {
                            // Snap: future day → clamp to today
                            setToDay(todayDayNum - 1);
                          } else {
                            setToDay(index);
                          }
                        }}
                        label="DAY"
                        isActive={true}
                      />
                    );
                    return (
                      <DatePickerWheel
                        key="YYYY"
                        items={years}
                        selectedIndex={toYear}
                        onSelect={(index: number) => {
                          const selectedYearNum = parseInt(years[index]);
                          if (selectedYearNum > todayYearNum) {
                            // Snap: future year → clamp to today entirely
                            setToYear(todayYearIndex);
                            setToMonth(todayMonthNum);
                            setToDay(todayDayNum - 1);
                          } else {
                            setToYear(index);
                            let newMonth = toMonth;
                            if (selectedYearNum === todayYearNum && toMonth > todayMonthNum) {
                              newMonth = todayMonthNum;
                              setToMonth(newMonth);
                            }
                            const newMaxDays = getDaysInMonth(newMonth, index);
                            let adjustedDay = adjustDayIfNeeded(toDay, newMaxDays);
                            if (selectedYearNum === todayYearNum && newMonth === todayMonthNum && adjustedDay > todayDayNum - 1) {
                              adjustedDay = todayDayNum - 1;
                            }
                            if (adjustedDay !== toDay) setToDay(adjustedDay);
                          }
                        }}
                        label="YEAR"
                        isActive={true}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {(() => {
          const isCustomRange = selectedDateType === "Custom Range";
          const day = parseInt(fromDays[fromDay], 10);
          const month = fromMonth + 1;
          const year = parseInt(years[fromYear], 10);

          const fromDate = new Date(year, month, day);

          const toDay_num = parseInt(toDays[toDay], 10);
          const toMonth_num = toMonth + 1;
          const toYear_num = parseInt(years[toYear], 10);
          const toDate = new Date(toYear_num, toMonth_num, toDay_num);

          const isInvalidRange = isCustomRange && fromDate > toDate;

          // Boundary validations (month is 0-indexed in Date constructor)
          const fromDateCorrect = new Date(year, fromMonth, day);
          const toDateCorrect = new Date(toYear_num, toMonth, toDay_num);

          const todayEnd = new Date();
          todayEnd.setHours(23, 59, 59, 999);
          const isEndAfterToday = isCustomRange && toDateCorrect > todayEnd;

          const minDateStr = currentTab === "cough" ? oldestCoughDate : oldestClearDate;
          const minDate = minDateStr ? new Date(minDateStr + "T00:00:00") : null;
          const isStartBeforeMin = isCustomRange && minDate !== null && fromDateCorrect < minDate;

          const isDisabled = isInvalidRange || isEndAfterToday || isStartBeforeMin;

          const validationError = isInvalidRange
            ? "Start date must be before end date"
            : isEndAfterToday
              ? "End date cannot be in the future"
              : isStartBeforeMin
                ? `Start date cannot be before first therapy (${formatISOByConfig(minDateStr, DATE_FORMAT)})`
                : "";

          return (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              {validationError && (
                <p style={{ color: "#E53935", fontSize: "13px", margin: "11px 5%", textAlign: "center" }}>
                  {validationError}
                </p>
              )}
              <button
                onClick={() => {
                  onselectRange();
                  setShowDateModal(false);
                }}
                style={{
                  width: "90%",
                  padding: "12px",
                  backgroundColor: isDisabled ? "#808587" : "#05ACD9",
                  color: "#fff",
                  border: "none",
                  borderRadius: "24px",
                  fontSize: "16px",
                  fontWeight: "500",
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  marginTop: "16px",
                  marginBottom: "20px",
                }}
                disabled={isDisabled}
              >
                Save
              </button>
            </div>
          );
        })()}
      </div>
    </IonModal>
  );

  return (
    <IonPage>
      <Header
        showGreeting={false}
        showUserName={false}
        showAvatar={false}
        pageTitle="Send Report"
        subTitle={userName}
        showUserIcon={false}
        showBackButton={true}
        onBackButtonClick={() => window.history.back()}
      />

      <IonContent className="account-content">
        <div style={{ paddingTop: "16px" }}>
          {availableTabs.length > 1 && (
            <TabNavigation
              tabs={availableTabs}
              activeTab={currentTab}
              onTabChange={setCurrentTab}
              className="font-bold"
            />
          )}
        </div>
        {/* Login & Security Section */}
        <div className="account-section-header">
          <IonText>
            <h2 className="settings-title">Report Data</h2>
          </IonText>
        </div>

        <div className="profile-card">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              position: "relative",
              opacity: loading ? 0.6 : 1,
              pointerEvents: loading ? "none" : "auto",
            }}
            onClick={() => {
              if (!loading) setShowDateModal(true);
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "5px",
                flex: 1,
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <h2
                  style={{
                    fontFamily: "Figtree",
                    fontWeight: "400",
                    fontSize: "17px",
                    lineHeight: "22px",
                    letterSpacing: "0px",
                    color: "#000000",
                    margin: 0,
                  }}
                >
                  Date Range
                </h2>
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <p className="phone" style={{ margin: 0 }}>
                  {selectedDateType === "Custom Range"
                    ? `${formatISOByConfig(dateRange.split(" to ")[0], DATE_FORMAT)} to ${formatISOByConfig(dateRange.split(" to ")[1], DATE_FORMAT)}`
                    : selectedDateType}
                </p>
              </div>
            </div>
            {/* Chevron icon to open modal */}
            <IonIcon
              icon={chevronForward}
              className="chevron-icon"
              style={{
                marginLeft: "12px",
                marginRight: "0",
                fontSize: "32px",
                width: "32px",
                height: "32px",
                cursor: loading ? "not-allowed" : "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (!loading) setShowDateModal(true);
              }}
            />
          </div>
        </div>
        {refIDLoading && (
          <div
            style={{
              padding: "3px",
              paddingTop: "20px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              gap: 8,
            }}
          >
            <img
              src={loadingAnimation}
              alt="Loading..."
              style={{
                width: 20,
                height: 20,
                color: "#05ACD9",
                filter:
                  "brightness(0) saturate(100%) invert(52%) sepia(94%) saturate(1304%) hue-rotate(159deg) brightness(95%) contrast(101%)",
                animation: "spin 1s linear infinite",
              }}
            />
            <p style={{ margin: 0 }}>Loading therapies...</p>
            <style>
              {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
            </style>
          </div>
        )}
        {!refIDLoading && allRefIds.length === 0 && (
          <div
            style={{
              padding: "3px",
              paddingTop: "20px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <p style={{ margin: 0 }}>No therapies available in this date range.</p>
          </div>
        )}        
          <div>
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "2px 4px 16px 16px",
                  marginLeft: "3%",
                  marginRight: "3%",
                  marginBottom: "12px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  {/* Left: Filter by Ref IDs */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "10px",
                      marginLeft: "8px",
                      maxWidth: "160px",
                    }}
                  >
                    <input
                      type="checkbox"
                      id="refIdFilter"
                      style={{
                        cursor: "pointer",
                        width: "20px",
                        height: "20px",
                        accentColor: "#05ACD9",
                      }}
                      disabled={loading}
                      checked={open}
                      onChange={() => {
                        if (!loading) {
                          setOpen(!open);
                          if (open) {
                            setSelectedIds([]);
                          }
                        }
                      }}
                    />
                    <label
                      htmlFor="refIdFilter"
                      style={{
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#333",
                      }}
                    >
                      Select Therapies to Graph
                    </label>
                  </div>
                  {/* Right: Graph select */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px", alignItems: "flex-end" }}>
                    <label
                      style={{
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#333",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Metric to trend
                    </label>
                    <CustomDropdown
                      selected={selectedGraph}
                      onChange={(value: string) => {
                        setSelectedGraph(value);
                      }}
                      options={graphSpecific}
                      textSizeClass="text-sm"
                    />
                  </div>
                </div>
              </div>
              {alertMessage && (
                <div
                  style={{
                    color: "red",
                    fontSize: "12px",
                    marginLeft: "10px",
                    opacity: 1,
                    transition: "opacity 0.5s",
                    animation: "fadeOutAlert 5s forwards",
                  }}
                >
                  {alertMessage}
                  <style>
                    {`
              @keyframes fadeOutAlert {
                0% { opacity: 1; }
                80% { opacity: 1; }
                100% { opacity: 0; }
              }
              `}
                  </style>
                </div>
              )}
              {open &&
                (refIDLoading ? (
                  <div
                    style={{
                      backgroundColor: "#fcfcfc",
                      borderRadius: "8px",
                      padding: "20px",
                      textAlign: "center",
                      color: "#666",
                      fontSize: "14px",
                      marginLeft: "3%",
                      marginRight: "3%",
                    }}
                  >
                    Loading Ref IDs...
                  </div>
                ) : allRefIds.length === 0 ? (
                  <div
                    style={{
                      backgroundColor: "#fcfcfc",
                      borderRadius: "8px",
                      padding: "20px",
                      textAlign: "center",
                      color: "#666",
                      fontSize: "14px",
                      marginLeft: "3%",
                      marginRight: "3%",
                    }}
                  >
                    No therapies in this range
                  </div>
                ) : (
                  <div
                    style={{
                      marginLeft: "3%",
                      marginRight: "3%",
                      backgroundColor: "#fcfcfc",
                      borderRadius: "12px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        padding: "10px 10px 6px",
                        borderBottom: "1px solid #ebebeb",
                      }}
                    >
                      <input
                        type="text"
                        placeholder="🔍  Search by date, time or ref ID"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          borderRadius: "20px",
                          border: "1px solid #e0e0e0",
                          backgroundColor: "#fff",
                          fontSize: "13px",
                          outline: "none",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        overflowY: "auto",
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        padding: "10px",
                        maxHeight: "min(250px, 30vh)",
                        WebkitOverflowScrolling: "touch",
                      }}
                    >
                      {allRefIds
                        .filter((id: ids) => {
                          const term = searchTerm.toLowerCase();
                          const formattedDate = convertShortDateFormat(id.startTime || "").toLowerCase();
                          return (
                            (id.refId || "").toLowerCase().includes(term) ||
                            (id.deviceId || "").toLowerCase().includes(term) ||
                            (id.startTime || "").toLowerCase().includes(term) ||
                            formattedDate.includes(term)
                          );
                        })
                        .map((id: ids) => {
                          const isSelected = selectedIds.includes(id.refId);
                          const startTime = convertShortDateFormat(
                            id.startTime ? id.startTime : "",
                          );
                          return (
                            <span
                              key={id.refId}
                              onClick={() => toggleSelect(id.refId)}
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "4px",
                                padding: "12px 20px",
                                cursor: "pointer",
                                borderRadius: "25px",
                                backgroundColor: isSelected ? "#42c7e8" : "#fff",
                                color: isSelected ? "#fff" : "#333",
                                userSelect: "none",
                                transition: "all 0.3s ease",
                                boxShadow: isSelected
                                  ? "0 4px 15px rgba(255, 255, 255, 0.4)"
                                  : "0 2px 8px rgba(0, 0, 0, 0.08)",
                                width: "100%",
                                boxSizing: "border-box",
                              }}
                            >
                              <div style={{ fontWeight: "600", fontSize: "14px" }}>
                                Time: {startTime}
                              </div>
                              <div
                                style={{
                                  fontWeight: "400",
                                  fontSize: "12px",
                                  opacity: isSelected ? 0.9 : 0.7,
                                }}
                              >
                                Device id: {id.deviceId}
                              </div>
                              <div
                                style={{
                                  fontWeight: "400",
                                  fontSize: "12px",
                                  opacity: isSelected ? 0.9 : 0.7,
                                }}
                              >
                                {" "}
                                Ref Id: {id.refId}
                              </div>
                              <div
                                style={{
                                  fontWeight: "400",
                                  fontSize: "12px",
                                  opacity: isSelected ? 0.9 : 0.7,
                                }}
                              >
                                Mode: {id.mode}
                              </div>
                            </span>
                          );
                        })}
                    </div>
                  </div>
                ))}
            </div>
            {showDeletePdf && renderDeletePdf(selectedPdfNames)}
            {dateRange && (
              <div
                className="profile-card"
                style={{
                  cursor: "pointer",
                  padding: "16px",
                  marginBottom: "16px",
                  marginTop: "16px",
                }}
              >
                {selectedIds.length > 0 && (
                  <div style={{ fontSize: "14px", fontWeight: 500, color: "#333" }}>
                    <span
                      style={{ fontWeight: 600, fontSize: "16px", color: "#333" }}
                    >
                      Selected Ref IDs:{" "}
                    </span>

                    <span style={{ fontSize: "14px", color: "#555" }}>
                      {(showAllRefIds ? selectedIds : selectedIds.slice(0, 6)).join(
                        ", ",
                      )}
                    </span>

                    {selectedIds.length > 6 && (
                      <span
                        style={{
                          color: "#05ACD9",
                          cursor: "pointer",
                          marginLeft: "6px",
                          fontWeight: 600,
                          fontSize: "14px",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowAllRefIds(!showAllRefIds);
                        }}
                      >
                        {showAllRefIds
                          ? " Show less"
                          : ` +${selectedIds.length - 6} more`}
                      </span>
                    )}
                    <br />
                  </div>
                )}
                <IonButton
                  style={{
                    "--border-radius": "30px",
                    "--background":
                      !selectedDateType || allRefIds.length === 0 || loading
                        ? "#44aed3"
                        : "#05ACD9",
                    height: "52px",
                    color: "white",
                    fontFamily: "Figtree",
                    fontWeight: "600",
                    fontSize: "16px",
                    margin: "16px",
                    width: "calc(100% - 32px)",
                  }}
                  expand="block"
                  onClick={() => {
                    handleGenerateReport();
                  }}
                  disabled={loading || allRefIds.length === 0}
                >
                  {loading ? (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <img
                        src={loadingAnimation}
                        alt="Loading..."
                        style={{
                          width: 20,
                          height: 20,
                          animation: "spin 1s linear infinite",
                        }}
                      />
                      Generating...
                      {downloadProgress > 0 && downloadProgress < 100 && (
                        <span style={{ marginLeft: 8, fontSize: 14 }}>
                          {downloadProgress}%
                        </span>
                      )}
                      <style>
                        {`
                      @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                      }
                    `}
                      </style>
                    </span>
                  ) : (
                    "Generate Report"
                  )}
                </IonButton>
              </div>
            )}
          </div>
      
        <div className="account-section-header">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              margin: "8px 0 16px 0",
              padding: "0 8px",
            }}
          >
            <IonText>
              <h2 className="settings-title">Generated PDFs</h2>
            </IonText>
          </div>
        </div>
        {allPDFs && allPDFs.length > 0 ? (
          <>
            {/* Downloads Header with Multi-Select Actions */}

            {/* PDF List with Multi-Select on Long Press */}
            {allPDFs.map((file, i) => {
              // Helper to format bytes to human readable size
              const formatSize = (bytes?: number) => {
                if (!bytes || isNaN(bytes)) return "";
                if (bytes < 1024) return `${bytes} B`;
                if (bytes < 1024 * 1024)
                  return `${(bytes / 1024).toFixed(1)} KB`;
                return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
              };
              // Helper to format ctime
              const formatCtime = (ctime?: number) => {
                if (!ctime) return "";
                const d = new Date(ctime);
                return formatISOByConfig(d.toISOString().split("T")[0], "MMM DD YYYY");
              };

              return (
                <div
                  key={file.name || i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 16px 28px 16px", // extra bottom padding for info row
                    borderRadius: "10px",
                    background: "#fff",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                    marginBottom: "16px",
                    gap: 12,
                    border: "1px solid #eee",
                    userSelect: "none",
                    marginLeft: "3%",
                    marginRight: "3%",
                    position: "relative",
                  }}
                  onClick={() => {
                    // If in selection mode, toggle selection
                    if (selectedPdfNames?.length > 0) {
                      setSelectedPdfNames((prev) =>
                        prev.includes(file.name)
                          ? prev.filter((n) => n !== file.name)
                          : [...prev, file.name],
                      );
                    }
                  }}
                >
                  {/* Icon area: PDF icon if not selected, tick if selected */}
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      background: "#E53935",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      fontSize: 16,
                      flex: "0 0 40px",
                      marginRight: 12,
                      transition: "background 0.2s",
                    }}
                  >
                    PDF
                  </div>
                  <span
                    style={{
                      fontSize: "15px",
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontWeight: 500,
                      color: "#222",
                    }}
                  >
                    {file.name}
                  </span>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 16,
                    }}
                  >
                    <IonIcon
                      icon={eyeOutline}
                      style={{
                        fontSize: "22px",
                        color: "#05ACD9",
                        cursor: "pointer",
                        transition: "color 0.2s",
                      }}
                      onClick={async (e) => {
                        e.stopPropagation();
                        handleOpenPdf(e, file.name);
                      }}
                      aria-label="View PDF"
                    />

                    <IonIcon
                      icon={shareOutline}
                      style={{
                        fontSize: "22px",
                        color: "#4CAF50",
                        cursor: "pointer",
                        transition: "color 0.2s",
                      }}
                      onClick={async (e) => {
                        e.stopPropagation();
                        handleSharePdf(e, [file.name]);
                      }}
                      aria-label="Share PDF"
                    />
                    <IonIcon
                      icon={trashOutline}
                      style={{
                        fontSize: "22px",
                        color: "#E53935",
                        cursor: "pointer",
                        transition: "color 0.2s",
                      }}
                      onClick={async (e) => {
                        e.stopPropagation();
                        setShowDeletePdf(true);
                        setSelectedPdfNames([file.name]);
                      }}
                      aria-label="Delete PDF"
                    />
                  </div>
                  {/* Info row: size (left), ctime (right) */}
                  <div
                    style={{
                      position: "absolute",
                      left: 16,
                      right: 16,
                      bottom: 8,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      fontSize: "12px",
                      color: "#888",
                      pointerEvents: "none",
                    }}
                  >
                    <span style={{ marginLeft: 2 }}>
                      {formatSize(file.size)}
                    </span>
                    <span style={{ marginRight: 2 }}>
                      {formatCtime(file.ctime)}
                    </span>
                  </div>
                </div>
              );
            })}
          </>
        ) : (
          <div
            style={{
              backgroundColor: "#fcfcfc",
              borderRadius: "8px",
              padding: "20px",
              textAlign: "center",
              color: "#666",
              fontSize: "14px",
              marginLeft: "3%",
              marginRight: "3%",
            }}
          >
            No reports generated yet
          </div>
        )}

        {/* Date Range Modal */}
        {showDateModal === true && renderDateRangeModal()}
      </IonContent>
    </IonPage>
  );
};

export default SendReport;
